import { captureExceptionSafe } from './captureExceptionSafe';

/**
 * Decodes a JWT token to extract the expiration time
 * @param {string} token - The JWT token
 * @returns {number|null} - The expiration timestamp in milliseconds, or null if invalid
 */
export const getTokenExpiration = token => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if necessary for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(
      paddedPayload.replace(/-/g, '+').replace(/_/g, '/'),
    );

    const parsedPayload = JSON.parse(decodedPayload);

    // JWT exp field is in seconds since epoch, convert to milliseconds
    if (parsedPayload.exp && typeof parsedPayload.exp === 'number') {
      return parsedPayload.exp * 1000;
    }

    return null;
  } catch (error) {
    captureExceptionSafe(error, {
      tags: {
        component: 'jwt-utils',
        operation: 'decode-token-expiration',
      },
      extra: {
        tokenLength: token ? token.length : null,
        tokenPrefix: token ? token.substring(0, 50) : null,
      },
    });
    return null;
  }
};

/**
 * Checks if a JWT token is expired or will expire soon
 * @param {string} token - The JWT token
 * @param {number} bufferMinutes - Minutes before expiration to consider "expired" (default: 5)
 * @returns {boolean} - True if token is expired or will expire soon
 */
export const isTokenExpiringSoon = (token, bufferMinutes = 5) => {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return true; // If we can't read expiration, consider it expired
  }

  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() + bufferMs >= expiration;
};

/**
 * Gets the actual expiration time for a token, falling back to hard-coded values if JWT decode fails
 * @param {string} token - The JWT token
 * @param {number} fallbackMinutes - Fallback expiration in minutes (default: 60)
 * @returns {number} - Expiration timestamp in milliseconds
 */
export const getTokenExpirationWithFallback = (token, fallbackMinutes = 60) => {
  const jwtExpiration = getTokenExpiration(token);

  if (jwtExpiration) {
    return jwtExpiration;
  }

  // Fallback to hard-coded value if JWT decode fails
  captureExceptionSafe(
    new Error('JWT token expiration decode failed, using fallback'),
    {
      tags: {
        component: 'jwt-utils',
        operation: 'fallback-expiration',
      },
      extra: {
        tokenLength: token ? token.length : null,
        tokenPrefix: token ? token.substring(0, 50) : null,
        fallbackMinutes,
      },
    },
  );
  return Date.now() + fallbackMinutes * 60 * 1000;
};
